import { Injectable } from '@angular/core';
import * as L from 'leaflet'; // ensure this is at top
import 'leaflet-wms-header';
import { createNavigationGlobalIcon } from '../shared/service/icon.service';
import { environment } from 'src/environments/environment';
type AnyLayer = L.Layer

export interface FeedUI {           // the hint text
  showFilterIcon: boolean;   
}

export interface FeedViewModel {
  sections: FeedSection[];
  ui: FeedUI;
}

export interface FeedLine {
  text: string;
  subText?: string;
  legendColor?: string;
  iconUrl?: string;
  style?: { [k: string]: string };
}
export interface FeedSection {
  show: boolean;
  className: string;
  style?: { [k: string]: string };
  items: FeedLine[];
}
export interface BuildFeedSectionsMeta {
  featureCount?: number;
  showCountRow?: boolean;   // <= only override when this is true
}

export interface BuildFeedSectionsMeta { featureCount?: number }

@Injectable({ providedIn: 'root' })
export class HelperService {

  private ensureBoundsPane(map: L.Map, paneName = 'wms-bounds-pane'): string {
    let pane = map.getPane(paneName);
    if (!pane) {
      pane = map.createPane(paneName);
    }

    pane.style.zIndex = '9999';
    pane.style.pointerEvents = 'none';
    return paneName;
  }

  private attachBoundsOverlay(map: L.Map, wmsLayer: L.Layer, rawBounds: any): void {
    if (!rawBounds) {
      return;
    }

    const bounds = L.latLngBounds(rawBounds);
    if (!bounds.isValid()) {
      return;
    }

    const existingOverlay = (wmsLayer as any).__boundsOverlay as L.Rectangle | undefined;
    const existingRemoveHandler = (wmsLayer as any).__boundsOverlayRemoveHandler as
      | (() => void)
      | undefined;

    if (existingRemoveHandler) {
      wmsLayer.off('remove', existingRemoveHandler);
    }

    if (existingOverlay && map.hasLayer(existingOverlay)) {
      map.removeLayer(existingOverlay);
    }

    const boundsOverlay = L.rectangle(bounds, {
      pane: this.ensureBoundsPane(map),
      color: '#ffd400',
      weight: 2,
      fill: false,
      opacity: 1,
      interactive: false,
    });

    boundsOverlay.addTo(map);
    boundsOverlay.bringToFront();

    const removeHandler = () => {
      const overlay = (wmsLayer as any).__boundsOverlay as L.Rectangle | undefined;
      if (overlay && map.hasLayer(overlay)) {
        map.removeLayer(overlay);
      }
    };

    (wmsLayer as any).__boundsOverlay = boundsOverlay;
    (wmsLayer as any).__boundsOverlayRemoveHandler = removeHandler;

    wmsLayer.on('remove', removeHandler);
  }

  private safeAssign(
  label: string,
  value: unknown,
  suffix = '',
  extraVisible?: boolean,     // 👈 new flag
  labelWhenEmpty?: string,     // 👈 optional override label when no value
): string {
  const hasValue = value !== null && value !== undefined && value !== '';
  if (hasValue) {
    return `${label} : ${value}${suffix}`;
  }
  if (extraVisible) {
    return labelWhenEmpty ?? label; // show label even without value
  }
  return '';
}


private pushIf(
    items: FeedLine[],
    label: string,
    value: unknown,
    opts?: 
    { suffix?: string; 
      color?: string, 
      visibleIf?: boolean;   
      labelWhenEmpty?: string; 
      iconUrl?: string;
      style?: { [k: string]: string };
    }
  ) {
    const text = this.safeAssign(label, value, opts?.suffix ?? '',  opts?.visibleIf, opts?.labelWhenEmpty);
    if (text) items.push({ text, legendColor: opts?.color, iconUrl: opts?.iconUrl, style: opts?.style });
  }


getFormattedArea(data: any, metric: string): string {
  if (data == null || data === 'N/A' || data === '') return '';
  const raw = String(data);
  // grab the first number (handles NBSP, commas, etc.)
  const m = raw.match(/[+-]?\d+(?:[.,]\d+)?/);
  if (!m) return '';
  const numberPart = Math.ceil(parseFloat(m[0].replace(/,/g, '')));
  const formatted = numberPart.toLocaleString('en-US');
  return `${formatted} ${metric}`;
}

buildFeedSections(config: { type?: string, color?: string, color_legend?: string }, filteredCollection: any, color?: string): FeedViewModel {
    const sections: FeedSection[] = []; {
    const defaultSection: FeedSection = {
      show: false,
      className: 'feed-display feed-class',
      items: [],
    };

    const blackSection: FeedSection = {
      show: false,
      className: 'feed-display oil-class',
      style: { 'background-color': 'black', padding: '7px' },
      items: [],
    };
     const ui: FeedUI = {
      showFilterIcon: false,
    };
    const resolveScalarColor = (fallback: string) => color ?? config?.color ?? fallback;


    switch (config?.type) {
      case 'Tree counting':
        {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), 'Tree crown area', config);
        ui.showFilterIcon = false;  
        break;
      }
       case 'Ship': {
         this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), 'Vessel Count', config);
         ui.showFilterIcon = true;  
        break;
      }
       case 'Transhipment': {
         this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), 'Transhipment Count', config);
         ui.showFilterIcon = true;  
        break;
      }
      case 'Deforestation': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), 'Deforested area', config);
        ui.showFilterIcon = false;  
        break;
      }

      case 'Oil': {
       this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), "Oil spill area", config);
       ui.showFilterIcon = false;  
      break;
      }
      case 'Path-SAR': {
        const defaultColor = resolveScalarColor('#ba5a06');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Unpaved road (SAR)", config);
        break;
      }
      case 'Plantation-SAR': {
        const defaultColor = resolveScalarColor('#e2e90bff');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Blank spot detection - SAR", config);
        break;
      }
      case 'Flood': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), "Flood extent", config);
        ui.showFilterIcon = false;  
        break;
      }
      case 'Path': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), "Path (Unmetalled road)", config);
        ui.showFilterIcon = false;  
        break;
      }

      case 'Plantation': {
        const defaultColor = resolveScalarColor('#e2e90bff');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Plantation area", config);
        break;
      }
      case 'vessel_prediction': {
        const defaultColor = resolveScalarColor('#e2e90bff');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "vessel_prediction", config);
        break;
      }
      case 'Vessel-classification': {
        const defaultColor = config.color_legend ?? '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "vessel_classification", config);
        break;
      }
      case 'Vehicle-classification': {
        const defaultColor = config.color_legend ?? '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "vehicle_classification", config);
        break;
      }
      case 'Road-classification': {
        const defaultColor = config.color ?? '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "road_classification", config);
        break;
      }
      case 'Land use/land cover detection': {
        const defaultColor = config.color ?? '#228B22';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Land use/land cover", config);
        break;
      }
      case 'Container detection': {
        this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '#800080', 'Container count', config);
        ui.showFilterIcon = false;
        break;
      }

      case 'Tuna-detection': {
        const defaultColor = resolveScalarColor('#e2e90bff');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Tuna stock", config);
        break;
      }
      case 'Container detection': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor('#800080'), 'Container count', config);
        ui.showFilterIcon = false;
        break;
      }
      case 'Oil spill type': {
       this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor('MAGENTA'), 'Oil spill type', config);
        ui.showFilterIcon = false;
        break;
      }
      case 'Flood Inundation': {
        this.buildFloodInundationSection(defaultSection, filteredCollection, config);
        ui.showFilterIcon = false;
        break;
      }
      case 'small-boat': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor('#e2e90bff'), 'Small boat count', config);
        ui.showFilterIcon = false;
        break;
      }
    }

    if (defaultSection.show) sections.push(defaultSection);
    if (blackSection.show) sections.push(blackSection);

    return { sections, ui };
}
}



private readonly prettyLabel = (k: string) =>
  k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

buildKeyValueSection(
  section: FeedSection,
  filteredCollection: any,
  defaultColor: string,
  emptyFallbackLabel: string,  
  config?: { type?: string , color?: any },      
) {

let entries: [string, any][] = [];

if (filteredCollection && typeof filteredCollection === 'object') {
  entries = Object.entries(filteredCollection);
} else {
  entries = [];
}

  // For Land use/land cover: show class legend with area from feed_results.area
  if (config?.type === 'Land use/land cover detection') {
    const colorMap = config.color;
    if (colorMap && typeof colorMap === 'object') {
      const areaData = (filteredCollection && typeof filteredCollection === 'object')
        ? (filteredCollection?.area || {}) : {};
      const unit = (filteredCollection && typeof filteredCollection === 'object')
        ? (filteredCollection?.unit || 'sqm') : 'sqm';
      const hasArea = Object.keys(areaData).length > 0;
      const labelToAreaKey: Record<string, string> = {
        'Built-up': 'built_up',
        'Roads': 'road',
        'Water': 'water',
        'Open area': 'open_area',
        'Agriculture/fallow land': 'agriculture',
        'Tree/Forest': 'tree/forest',
        'Plantation': 'plantation',
      };
      for (const classKey of Object.keys(colorMap)) {
        const classInfo = colorMap[classKey];
        let subText: string | undefined;
        if (hasArea) {
          const areaKey = labelToAreaKey[classInfo.label];
          const areaValue = areaKey != null ? areaData[areaKey] : undefined;
          if (areaValue != null) {
            subText = `Total Area: ${this.getFormattedArea(areaValue, unit)}`;
          }
        }
        section.items.push({ text: classInfo.label, legendColor: classInfo.color, subText });
      }
      section.show = section.items.length > 0;
      return;
    }
  }

  // For Road-classification and Tuna-detection: entries are empty (feed result is null)
  if (
    config?.type === 'Road-classification' ||
    config?.type === 'Tuna-detection'
  ) {
    // Use config.color for legend display
    const colorMap = config.color;
    if (colorMap && typeof colorMap === 'object') {
      for (const classKey of Object.keys(colorMap)) {
        const classInfo = colorMap[classKey];
        this.pushIf(section.items, classInfo.label, '', {
          color: classInfo.color,
          visibleIf: true,
          labelWhenEmpty: classInfo.label,
        });
      }
      section.show = section.items.length > 0;
      return;
    }
  }

  // Empty -> show a single fallback row
if (entries.length === 0 ) {
    this.pushIf(section.items, emptyFallbackLabel, "", {
      color: defaultColor,
      visibleIf: true,
      labelWhenEmpty: emptyFallbackLabel,
    });
    section.show = true;
    return;
}


for (const [key, value] of entries) {
  // 🔹 CASE 1: value is an object with a `metric` -> expand it
  if (value && typeof value === 'object' && 'metric' in value) {
    const metric = value.metric;
    for (const [innerKey, innerValue] of Object.entries(value)) {
      if (innerKey === 'metric') continue; // skip metric itself

      const label = this.prettyLabel(innerKey); // e.g. plantable_area -> Plantable Area
      let display: string;

       if (config?.type === 'Oil' || config?.type === 'Oil spill type') {
        display = this.getFormattedArea(innerValue, metric);
       } else if (config?.type === 'Plantation' || config?.type === 'Plantation-SAR') {
       display = this.roundWithUnit(innerValue, 2, metric);
       } else {
       display = String(value ?? '');
     }
      this.pushIf(section.items, label, display, {
        color: config?.type !== 'Vessel-classification' ? defaultColor : defaultColor[key]?.color_legend,
        visibleIf: true,
        labelWhenEmpty: label,
      });
    }
    continue;
  }

 if (key === 'location_after_one_hour') {
  const canvas = createNavigationGlobalIcon('Yellowgreen',
        'dotted'
      );

 const iconUrl = canvas.toDataURL();
  this.pushIf(
    section.items,
    'Predicted vessel location',
    '',
    {
      color: defaultColor,
      visibleIf: true,
      labelWhenEmpty: 'Predicted vessel location',
      iconUrl: iconUrl
    }
  );

  continue;
 }

  const label = this.prettyLabel(key);
  const isClassBasedLookup =
    config?.type === 'Vessel-classification' || config?.type === 'Vehicle-classification';
  const legendColor = config?.type === 'small-boat' && key === 'sea_state'
    ? undefined
    : isClassBasedLookup ? (defaultColor as any)[key]?.color : defaultColor;
  const lineStyle = config?.type === 'small-boat' && key === 'sea_state'
    ? { 'padding-left': '28px' }
    : undefined;

  this.pushIf(section.items, label, value, {
    color: legendColor,
    visibleIf: true,
    labelWhenEmpty: label,
    style: lineStyle,
  });
}
section.show = section.items.length > 0;
}

roundWithUnit(value: any, decimals: number = 2, metric: string): string {
  let rounded = parseFloat(value).toFixed(decimals);
  return `${rounded} ${metric}`;
}

private buildFloodInundationSection(section: FeedSection, feedResults: any, config?: any): void {
  const colorMap = config?.color as Record<string, { label: string; color: string }> | undefined;

  // Show inundated area from feed_results.Inundated_area
  const rawArea = feedResults?.Inundated_area ?? feedResults?.inundated_area;
  if (rawArea != null) {
    const formattedArea = this.getFormattedArea(rawArea, 'sqm');
    section.items.push({ text: `Inundated Area : ${formattedArea}` });
  }

  // Show depth-class legend entries
  if (colorMap && typeof colorMap === 'object') {
    for (const entry of Object.values(colorMap)) {
      section.items.push({ text: entry.label, legendColor: entry.color });
    }
  } else {
    section.items.push({ text: 'Flood Inundation', legendColor: '#2E86C1', visibleIf: true } as any);
  }

  section.show = section.items.length > 0;
}

public buildTooltipContent(
  props: Record<string, unknown> = {},
  rawLen?: unknown,
  centroidCoordinates?: [number, number] | number[] | string
): string {
  const esc = (v: any) =>
    String(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const loc =
    Array.isArray(centroidCoordinates)
      ? centroidCoordinates.join(', ')       // original precision, lon,lat order
      : (centroidCoordinates ?? '');
  const locParts = String(loc).split(',').map(p => p.trim());

  const conf = props?.['confidence'];        // bracket access fixes index-signature warning
  const confidenceLine = conf != null ? `<b>Confidence:</b> ${esc(conf)}<br>` : '';
  const lengthLine     = rawLen ? `<b>Vessel length:</b> ${esc(rawLen)}<br>` : '';
  const lng   = loc !== '' ? `<b>Lng:</b> ${esc(locParts[0])}<br>` : '';
  const lat   = loc !== '' ? `<b>Lat:</b> ${esc(locParts[1])}<br>` : '';

  return `
    <div style="background-color:black;color:white;padding:5px;max-width:350px;">
      ${confidenceLine}${lengthLine}${lng}${lat}
    </div>
  `;
}

public computeAnchorAndLocation(
    feature: any,
    latlng: L.LatLng
  ): { anchor: L.LatLng; centroidCoordinates: [number, number] } {
    const t = (window as any).turf;
    // defaults: mouse position if nothing else works
    let centroidCoordinates: [number, number] = [latlng.lng, latlng.lat]; // [lon, lat]
    let anchor: L.LatLng = latlng;

    try {
      if (feature?.geometry?.type === 'Point') {
        const coords = feature.geometry.coordinates as [number, number];
        if (Array.isArray(coords) && coords.length >= 2) {
          centroidCoordinates = [coords[0], coords[1]];
        }
      } else if (t?.pointOnFeature) {
        const pt = t.pointOnFeature(feature);
        const coords = pt?.geometry?.coordinates as [number, number] | undefined;
        if (Array.isArray(coords) && coords.length >= 2) {
          centroidCoordinates = [coords[0], coords[1]];
        }
      }
      // keep anchor aligned with what we display (GeoJSON is [lon, lat])
      anchor = L.latLng(centroidCoordinates[1], centroidCoordinates[0]);
    } catch {
      // fall back already set
    }
    return { anchor, centroidCoordinates };
}

normalizeConfig(input: any) {
  const src = input?.config ?? input ?? {};
  const { type, filterKey, class: cls, color } = src;
  return { type, filterKey, class: cls, color };
}

classCqlStrict(cls: unknown, field = 'class'): string {
  const f = `"${field}"`;
  const s = String(cls ?? '').trim().toLowerCase();
  if (!s) return '';

  if (s === 'ship') {
    return `strToLowerCase(${f}) = 'ship'`;
  }
  if (s === 'transhipment' || s === 'transshipment') {
    // accept both spellings present in datasets
    return `strToLowerCase(${f}) IN ('transhipment','transshipment')`;
  }
  return ''; // do NOT coerce anything else into transhipment
}

getFeedResultKeyForClass(feedType?: string): 'vessel_count' | 'transhipment_count' {
  const s = String(feedType ?? '').trim().toLowerCase();
  return s.includes('trans') ? 'transhipment_count' : 'vessel_count';
}

combineFilters(...parts: Array<string | undefined | null>): string {
  const wrapIfOr = (s: string) =>
    /\bOR\b/i.test(s) && !(s.trim().startsWith('(') && s.trim().endsWith(')'))
      ? `(${s.trim()})` : s.trim();
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    const s = (p ?? '').trim();
    if (!s) continue;
    const norm = s.replace(/\s+/g, ' ').toLowerCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    out.push(wrapIfOr(s));
  }
  return out.join(' AND ');
}

makeWfsParams(layerName: string){
 return new URLSearchParams({
    service: 'WFS',
    version: '2.0.0',
    request: 'GetFeature',
    typeName: layerName,                 
    outputFormat: 'application/json',
    srsName: 'EPSG:4326'
  });
}
  validateCoordinates(longitude, latitude) {
    // Check if values are numbers
    if (isNaN(longitude) || isNaN(latitude)) {
      return false;
    }
    
    // Check longitude range (-180 to 180)
    if (longitude < -180 || longitude > 180) {
      return false;
    }
    
    // Check latitude range (-90 to 90)
    if (latitude < -90 || latitude > 90) {
      return false;
    }
    
    return true;
  }

  createWmsLayer(
  map: L.Map,
  urlData: any,
  shapeFile: string,
  zIndex: number = 1000,
  isValueAddedProduct: boolean = false,
  baseUrlValue?: string,
  pane?: string,  
  bandValue?: any
): AnyLayer   | null {
  if (!urlData?.layer && !isValueAddedProduct) return null;
  const layerObj = isValueAddedProduct ? urlData : urlData.layer;
  const bounds = layerObj.bounds;
  const baseUrl = environment.geoServerUrl;
  let layerName = layerObj.layerName ? layerObj.layerName : layerObj.layer;
  let bands = bandValue ? bandValue : layerObj.bands;
  let style = layerObj.tiffStyle;
  let algorithm = layerObj.algorithm;

  // Handle shapes overlay if shapeFile is not "image"
  if (shapeFile !== 'image' && Array.isArray(layerObj.shapes) && layerObj.shapes.length > 0) {
    const shapeNames = layerObj.shapes.map((s: any) => s.layerName).join(',');
    const shapeStyles = layerObj.shapes.map((s: any) => s.style).filter(Boolean).join(',');

    layerName += ',' + shapeNames; 
    if (shapeStyles) style += ',' + shapeStyles;
  }

  // Basic WMS params
  let params: any = {
    layers: layerName,
    format: 'image/png',
    transparent: true,
    maxZoom: 23,
    zIndex: zIndex,
    pane: pane || 'tilePane'
  };

  if (style) params.styles = style;

  let env = '';
 
  // Handle env (band min/max or multi-band)
  if (bands?.length === 1) {
    const { min, max } = bands[0];
    env += `minValue:${min};maxValue:${max};`;
  } else if (bands?.length > 1) {
    env += bands
      .map((b: any, i: number) => {
        let e = `b${i + 1}:${b.band};`;
        if (b.min != null && b.max != null) {
          e += `b${i + 1}minValue:${b.min};b${i + 1}maxValue:${b.max};`;
         }
        return e;
      })
      .join('');
    // params.env = env;
  }
  if (algorithm) {
    env += `algo:${algorithm};`;
  }
 
  if (env) {
    params.env = env;
  }
  // Create & add WMS layer
  const wmsLayer = this.createWmsLayerWithHeader(map, params);
  wmsLayer.addTo(map);
  this.attachBoundsOverlay(map, wmsLayer, bounds);

  if (bounds) {
    const leafletBounds = L.latLngBounds(bounds);
    if (leafletBounds.isValid()) {
      map.fitBounds(leafletBounds);
    }
  }
  return wmsLayer;
}
createWmsLayerWithHeader(
  map: L.Map,
  params: any
): L.TileLayer {
  const storedJwtToken = sessionStorage.getItem('jwttoken') || '';
  const sanitizedJwtToken = storedJwtToken.replace(/^"|"$/g, '');

  const headers = [
    { header: 'Authorization', value: `Bearer ${sanitizedJwtToken}` },
  ];

  const wmsHeaderFactory =
    (L as any).TileLayer?.wmsHeader || (L as any).tileLayer?.wmsHeader;

  if (!wmsHeaderFactory) {
    throw new Error('leaflet-wms-header plugin not loaded. Cannot send headers.');
  }

  const layer = wmsHeaderFactory(environment.geoServerUrl, params, headers, null) as L.TileLayer;
  return layer;
}

}


